import { PlayerModel } from '@toilet-paper-games/core';
import { action } from 'mobx';

import { Connection } from '@/common/models/Connection';
import { NetworkHandler, idPrefix } from '@/common/models/NetworkHandler';
import { makeSimpleAutoObservable } from '@/common/utils/mobx/mobx';
import { ControllerAppModel, SessionStorageKeys } from './ControllerAppModel';
import { NetworkDataType } from '@/common/models/NetworkDataTransfers';

export interface ConnectionMetadata {
  userId: string;
}

export class ControllerNetworkHandler extends NetworkHandler {
  hostConnection: Connection | null = null;
  hosterUuid: string | null = null;

  wasKicked = false;

  get user() {
    return this.appModel.user;
  }

  constructor(private appModel: ControllerAppModel) {
    super(`${appModel.uuid}-${Math.random().toString(36).substring(7)}`);
    makeSimpleAutoObservable(this);

    this.localConnection.on('open', () => {
      this.setupHosterConnection();
    });
  }

  get hasHostConnection() {
    return !!this.hostConnection;
  }

  setupHosterConnectionPromise: Promise<void> | null = null;

  setupHosterConnection(hosterUuid?: string) {
    if (this.setupHosterConnectionPromise) {
      return this.setupHosterConnectionPromise;
    }

    if (hosterUuid) {
      this.hosterUuid = hosterUuid;
      sessionStorage.setItem(SessionStorageKeys.HOSTER_UUID, hosterUuid);
    }

    if (!this.hosterUuid) return;
    // eslint-disable-next-line no-async-promise-executor
    this.setupHosterConnectionPromise = new Promise<void>(async (resolve, reject) => {
      await this.waitForOpen().catch((e) => reject(e));

      if (!this.localConnection.open) {
        reject('local connection not open');
        return;
      }

      if (!this.hosterUuid) {
        reject('no hosterUuid');
        this.setupHosterConnectionPromise = null;
        return;
      }

      const hosterUuid = this.hosterUuid.toLowerCase();

      this.hostConnection = new Connection(
        hosterUuid,
        this.localConnection.connect(idPrefix + hosterUuid, {
          metadata: {
            userId: this.appModel.uuid!,
          } satisfies ConnectionMetadata,
        }),
        this.appModel.uuid,
      );

      this.localConnection.on('error', (error) => {
        if (error.type === 'peer-unavailable' || error.type === 'server-error') {
          reject(error.type);
          this.setupHosterConnectionPromise = null;
        }

        if (error.type === 'unavailable-id') {
          console.error(
            'Connection ID is already taken, it is possible it never detected you disconnected',
          );
          reject(error.type);
          this.setupHosterConnectionPromise = null;
        }
      });

      this.hostConnection.raw.on('error', (error) => {
        if (error.type === 'connection-closed' || error.type === 'not-open-yet') {
          reject(error.type);
          this.setupHosterConnectionPromise = null;
        }
      });

      this.hostConnection.raw.on(
        'open',
        action(() => {
          this.networkReady = true;
          resolve();
          this.setupHosterConnectionPromise = null;
        }),
      );

      this.hostConnection.raw.on('data', (data) => {
        this.onData(data);
        this.hostConnection?.onData(data);
      });

      this.hostConnection.raw.on('close', () => {
        this.wasKicked = true;
      });
    });

    return this.setupHosterConnectionPromise;
  }
}
